import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbDate, NgbDateParserFormatter, NgbDatepicker, NgbDatepickerConfig, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { Select } from '@ngxs/store';
import { IUser } from 'projects/common/src/app/interfaces/user';
import { ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { Observable, Subscription } from 'rxjs';
import { IShift } from 'src/app/modules/core/interfaces/shift';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { CustomDatepickerFormaterService } from 'src/app/modules/core/services/custom-datepicker-formater.service';
import { CustomDatepickerI18nService, I18n } from 'src/app/modules/core/services/custom-datepicker-i18n.service';
import { dateToNgb, stringToNgb } from 'src/app/modules/core/services/datetime.service';
import { DayService } from 'src/app/modules/core/services/parameter/day/day.service';
import { DateFacade } from 'src/app/modules/core/store/date/date.facade';
import { DateState } from 'src/app/modules/core/store/date/date.state';

@Component({
  selector: 'app-layout-header',
  templateUrl: './layout-header.component.html',
  styleUrls: ['./layout-header.component.scss'],
  providers: [
    I18n,
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18nService },
    { provide: NgbDateParserFormatter, useClass: CustomDatepickerFormaterService },
  ],
})
export class LayoutHeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  @Select(DateState.UpdateSelectedDate) selectedDate$: Observable<any>;
  public currentDate: string;

  @ViewChild('datepickerContainer', { static: false })
  public datePickerView: ElementRef;

  @ViewChild('dp', { static: false })
  public datepicker: NgbDatepicker;

  @Input()
  public title: string;

  public detail: string;
  public icon: string;
  public toggleActive: boolean;
  public display: string;

  public isToday = false;

  private readonly MSG_LAST_SELECTION = 'last_selection';
  private newDaySubscription: Subscription;
  private dateSubscription: Subscription;
  private shiftSubscription: Subscription;
  private model$: NgbDate;
  set model(value: NgbDate) {
    this.currentDate = `${value.day < 10 ? `0${value.day}` : value.day}/`;
    this.currentDate += `${value.month < 10 ? `0${value.month}` : value.month}/${value.year}`;

    this.dateFacade.UpdateSelectedDate(value);

    this.model$ = value;
    this.checkIfToday(value); // Check if the selected date is today
    this.toggleCalendar(true);
  }
  get model(): NgbDate {
    return this.model$;
  }

  constructor(
    private parameterService: ParameterHandlerService,
    private ngbdatepickerConfig: NgbDatepickerConfig,
    private dayService: DayService,
    private textService: LanguageService,
    public dateFacade: DateFacade,
  ) {
    this.icon = 'dashboard';
    this.initDatePickerConfig(ngbdatepickerConfig);
  }

  private initDatePickerConfig(ngbdatepickerConfig?: NgbDatepickerConfig) {
    const user = this.parameterService.parameters.item('user-data').value as IUser;

    if (user.company.id === 2) {
      const today = new Date();
      today.setDate(today.getDate() + 1);

      this.ngbdatepickerConfig.maxDate = dateToNgb(today);
    } else {
      ngbdatepickerConfig.maxDate = dateToNgb(new Date());
    }

    ngbdatepickerConfig.navigation = 'arrows';
  }

  /**
   * Init app
   */
  public ngOnInit(): void {
    this.newDaySubscription = this.dayService.getObservable().subscribe((date: string) => {
      this.ngbdatepickerConfig.maxDate = stringToNgb(date);
    });

    this.shiftSubscription = this.parameterService.parameterSource.item('shift').subscribe((shift: IShift) => {
      this.refreshDetail();
    });
  }

  /**
   * After View Init
   */
  public ngAfterViewInit(): void {
    this.dateSubscription = this.parameterService.parameterSource.item('date').subscribe((date: string) => {
      setTimeout(() => {
        this.model = stringToNgb(date);
        this.refreshDetail();
      });
    });
  }

  /**
   * Destroy component
   */
  public ngOnDestroy(): void {
    this.newDaySubscription.unsubscribe();
    this.dateSubscription.unsubscribe();
    this.shiftSubscription.unsubscribe();
  }

  /**
   * Verifies if the date is today
   */
  private checkIfToday(selectedDate: NgbDate): void {
    const today = dateToNgb(new Date());
    this.isToday = selectedDate.equals(today);
  }

  /**
   * Refresh detail shift
   */
  private refreshDetail(): void {
    let shiftName = 'unknown';
    let datePresentationPipe = new DatePresentationPipe();

    if (this.parameterService.parameters.containsKey('shift')) {
      const shiftValue = this.parameterService.parameters.item('shift').value as IShift;
      if (shiftValue) {
        shiftName = shiftValue.name;
      }
    }
    const date = this.dateFacade.UpdateItemParameterService();
    const dateFormated = datePresentationPipe.transform(date);
    datePresentationPipe = null;

    if (shiftName === this.textService.message('day_detail')) {
      this.detail = `${this.textService.message('day_detail')} - ${dateFormated}`;
    } else {
      this.detail = this.textService.message(this.MSG_LAST_SELECTION, shiftName, dateFormated);
    }
  }

  /**
   * Select today in calendar
   */
  public today(): void {
    this.model = dateToNgb(new Date());
    this.ngbdatepickerConfig.maxDate = this.model;
    this.datepicker.navigateTo(this.model);
    this.checkIfToday(this.model); // Check if the selected date is today to disable the button
  }

  /**
   * Function to open or close calendar
   */
  public toggleCalendar(force?: boolean): void {
    const { style } = this.datePickerView.nativeElement;
    if (style.display === 'block' || force) {
      style.display = 'none';
    } else {
      style.display = 'block';
    }
  }
}
